import React from "react";
import { BreadcrumbItem } from "reactstrap";
import { Route, Switch, Link, Redirect } from "react-router-dom";
import * as Section from "@repoflow/components/section";
import SiteFilePublicSchema from "@common/schema/site-file/public";
import { isKind, KIND_CONFIG, IMAGES } from "@common/schema/site-file/kind";
import * as ListRoute from "./list";
import * as NewRoute from "./new";
import * as FileRoute from "./file";

// One kind of file — Images or Documents — and everything under it.
//
// `kind` enters the app here and every query beneath takes it as a variable
// alongside `siteId`, which is what makes the submenu the same boundary on the
// client that it is in the table: rows are partitioned by
// `${orgId}/${siteId}/${kind}`, so a listing is one partition and reaches
// nothing else.
const LocalProvider = ({
  match: {
    params,
    params: { kind },
  },
  children: Children,
}) => {
  const { base: parentBase, basepath: parentBasepath } =
    Section.useSectionContext();

  const base = `${parentBase}/${kind}`;
  const basepath = `${parentBasepath}/:kind`;

  return (
    <Section.Provider
      // Keyed on the kind, for the same reason the site route is keyed on
      // siteId: the screens below seed their form state once, at mount, and
      // moving between Images and Documents has to reset them rather than carry
      // one kind's half-filled form into the other's.
      key={kind}
      schema={SiteFilePublicSchema}
      base={base}
      basepath={basepath}
      attributes={params}
    >
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export function Content(props) {
  const {
    match: {
      params: { kind, siteId },
    },
  } = props;

  // A hand-typed or stale `:kind` is a redirect, not an error screen. The
  // router cannot constrain it — react-router 5 patterns match any segment —
  // and every query below would otherwise be sent a value the API rejects,
  // surfacing as a failed request on what looks like an ordinary tab.
  if (!isKind(kind)) {
    return <Redirect to={`/site/${siteId}/file/${IMAGES}`} />;
  }

  return (
    <LocalProvider {...props}>
      {({ basepath }) => (
        <Switch>
          <Route path={`${basepath}/new`} component={NewRoute.Content} />
          <Route path={`${basepath}/:fileId`} component={FileRoute.Content} />
          <Route path={`${basepath}/`} component={ListRoute.Content} />
        </Switch>
      )}
    </LocalProvider>
  );
}

export function Indice(props) {
  const {
    match: {
      params: { kind },
    },
  } = props;

  if (!isKind(kind)) return null;

  return (
    <LocalProvider {...props}>
      {({ base, basepath }) => (
        <>
          {/* The kind's own crumb, so the trail reads
              site › Files › Images › <file>. Labelled from the same
              declaration the tab strip above uses, rather than from the URL
              segment, so "images" is never shown to a person uncapitalised. */}
          <BreadcrumbItem>
            <Link to={base}>{KIND_CONFIG[kind].label}</Link>
          </BreadcrumbItem>
          <Switch>
            <Route path={`${basepath}/new`} component={NewRoute.Indice} />
            <Route path={`${basepath}/:fileId`} component={FileRoute.Indice} />
            <Route path={`${basepath}/`} component={ListRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}

export const Sidebar = () => <></>;
